import { Eye, Package } from 'lucide-react'
import { BASE_URL, EXTERNAL_BASE_URL } from '../apiConfig'
import { useTranslation } from '../LanguageContext'

export default function CardHome({ products, loading }) {
  const { t , lang } = useTranslation()

  if (loading) {
    const skeletonCount = 5;
    return (
      <>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
            </div>
          </div>
        ))}
      </>
    )
  }

  if (products?.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
          <Package className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">{t('card.no_products')}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('card.no_products_desc')}
        </p>
      </div>
    )
  }

  return (
    <>
      {products?.map((product) => (
        <div key={product.id} className="bg-white relative dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg py-4 px-3 hover:shadow-lg transition-shadow duration-200">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {product.image ? (
                  <img 
                    src={product.source_id ? `${EXTERNAL_BASE_URL}/storage/${product.image}` : `${BASE_URL}/storage/${product.image}`} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src = 'https://placehold.co/48x48?text=No+Image'
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <span className="text-xs text-gray-500">{t('card.no_image')}</span>
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate max-w-[180px]">
                  {product.name.length > 15
                    ? `${product.name.substring(0, 15)}`
                    : product.name}
                  </h3>
              </div>
            </div>
            <span className={`inline-flex absolute top-2 ${lang === 'ar' ? 'left-3' : 'right-3'} items-center px-2.5 py-[2px] rounded-full text-[10px] font-medium ${
              product.status === 'in_stock' 
                ? 'bg-green-600 text-white dark:bg-green-900 dark:text-green-200'
                : product.status === 'low_stock'
                ? 'bg-amber-500 text-white dark:bg-amber-900 dark:text-amber-200'
                : 'bg-red-500 text-white dark:bg-red-900 dark:text-red-200'
            }`}>
              {t('products.'+product.status)}
            </span>
          </div>

          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>{t('card.stock')} {product.stock}</span>
                <span>{Math.round((product.stock / 500) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    product.stock > 250 ? 'bg-green-500' :
                    product.stock > 50 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((product.stock / 500) * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600 dark:text-gray-400">{t('card.price')}</span>
              <span className="text-xs font-semibold text-gray-900 dark:text-gray-50">{product.price} MAD</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600 dark:text-gray-400">{t('card.last_updated')}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{product.last_updated}</span>
            </div>
          </div>
        </div>
      ))}
    </>
  )
}